// The app's half of server-sent alerts.
//
// Everything that decides WHAT to tell the server is pure and lives here;
// `src/app-state.js` owns the Vue refs, the store rows and the Capacitor
// plugin. The split is what makes this testable without a phone: the body sent
// to /api/push/subscribe is a function of a store snapshot, and that function
// can be checked against a literal.
//
// NO IMPORTS ON PURPOSE. This file is pulled into `app-state.js`, which is
// imported by the vitest suite in a plain Node environment — one import of
// `core/state.js` here (which touches `navigator` at module scope) and every
// test in the project stops running.

/**
 * Is this a token a push service actually issued?
 *
 * The app has always written a device row at the moment permission is granted,
 * with a made-up `local-ios-abc123` id standing in for a registration that did
 * not exist. That placeholder is still what the web gets, and it is the flag
 * that says "the server cannot reach this device" — so every decision about
 * whether the server or the app owns alerts runs through this one predicate
 * rather than re-deriving the prefix in three places.
 */
export function isServerToken(token) {
  const t = String(token ?? '');
  return t.length > 0 && !t.startsWith('local-') && t !== 'demo';
}

/**
 * Who owns disruption alerts on this device.
 *
 * Exactly one of the two must. The app scheduling its own notification while
 * the server is also pushing one is a double buzz for the same signal fault,
 * and the second one arrives with no way to tell it apart from a new problem.
 *
 * `subscribed` is TRI-STATE, and reading it as a boolean was the whole of the
 * duplicate-alert bug: `null` means no subscribe attempt has come back yet,
 * `true` means one returned 200, `false` means one demonstrably failed.
 *
 * The argument for `!== false` rather than `=== true`: a phone holding a
 * server token is a phone the server ALREADY has a subscription for and will
 * push to — the registration outlives the launch, and the boot subscribe only
 * re-confirms it. So the local path must stay quiet until a subscribe attempt
 * actually fails, not merely until one has not yet finished. Treating the
 * launch window as "not the server's" handed the local path a green light for
 * the second or two before the boot POST landed, and every disruption the
 * server had pushed while the app was closed was new to the local store: three
 * cancellations, pushed overnight, announced all over again at 07:22.
 *
 * A device that registered but could not reach the server still keeps the
 * local path, because a missed alert is the failure this app exists to
 * prevent. `false` is what says so, and only a failed attempt writes it.
 */
export function serverOwnsAlerts({ token, subscribed }) {
  return isServerToken(token) && subscribed !== false;
}

/**
 * May the APP raise its own alert on this phone?
 *
 * `serverOwnsAlerts` handed alerts back to the phone whenever a subscribe
 * failed, and that door was the whole of the paywall's bypass: a phone the
 * server refused (402, not entitled) would simply alert itself instead. So two
 * more answers close it:
 *
 *   * `refused` — the server said 402. It is not "could not reach", it is
 *     "no", and a no is not a reason to buzz locally.
 *   * `gated` — this build can sell (the Billing plugin is present) and the
 *     phone is not entitled. Nothing local fires.
 *
 * With neither, this is exactly the old rule: the local path runs only where
 * the server does not own alerts. A build with no Billing plugin never passes
 * `gated`, so it behaves as it always has.
 */
export function localMayAlert({ token, subscribed, refused = false, gated = false }) {
  if (gated || refused) return false;
  return !serverOwnsAlerts({ token, subscribed });
}

/**
 * Did a subscribe or register come back as the server's "not entitled"?
 *
 * NOT when the reason is `verification-unavailable`: that 402 means the server
 * could not reach Apple or Google to check, which says nothing about whether
 * this phone paid. It is treated like any other failure to subscribe — no
 * wall, no recorded lapse — and the next subscribe tries again.
 */
export function isNotEntitled(response) {
  if (response?.reason === 'verification-unavailable') return false;
  return response?.status === 402 || response?.error === 'not-entitled';
}

/**
 * The PTV ids a push says it was about, from any shape a platform delivers.
 *
 * `batchFor` in `server/push.mjs` sends `disruptionIds`, and the two platforms
 * hand it over differently. iOS passes the APNs payload's custom keys through
 * as an array under the notification's `data`; FCM's data block is strings
 * only, so the same list arrives as `"302511,302488"`, and a WebView that
 * JSON-encodes the block gives `"[302511,302488]"` or a `data` that is itself
 * a JSON string. A server old enough to predate the field sends nothing.
 *
 * Every one of those ends as a list of positive ints, and anything else — a
 * null, a word, a negative — ends as an empty one. This is what a push that
 * arrives while the app is open uses to stamp `sentAt` on the rows it named,
 * so the next poll cannot announce them a second time.
 */
export function pushedDisruptionIds(notification) {
  let data = notification?.data;
  if (typeof data === 'string') {
    try { data = JSON.parse(data); } catch { data = null; }
  }
  const raw = (data && typeof data === 'object' ? data.disruptionIds : undefined)
    ?? notification?.disruptionIds;

  let values = raw;
  if (typeof raw === 'string') {
    const text = raw.trim();
    if (!text) return [];
    try {
      const parsed = JSON.parse(text);
      values = Array.isArray(parsed) ? parsed : [parsed];
    } catch {
      values = text.split(',');
    }
  }
  if (!Array.isArray(values)) values = values == null ? [] : [values];
  return ids(values);
}

/** Ints, deduplicated, capped — the shape the server validates for. */
function ids(values, max = 50) {
  const out = [];
  for (const value of values ?? []) {
    const n = Number(value);
    if (!Number.isInteger(n) || n <= 0) continue;
    if (out.includes(n)) continue;
    out.push(n);
    if (out.length >= max) break;
  }
  return out;
}

/**
 * The subscribe body, from a snapshot of the store.
 *
 * `lines` are PTV route ids and `stations` are PTV stop ids, because those are
 * the only two identifiers the server and PTV both understand — the app's own
 * row ids mean nothing outside this phone.
 *
 * The device's UTC offset travels with it. Quiet hours are set in local time
 * ("don't wake me before six"), the sweep runs in Cloud Run's UTC, and without
 * the offset a Melbourne 22:00–06:00 window silences 08:00–16:00 AEST — the
 * exact hours the alerts are for.
 */
export function buildSubscribeBody({
  token,
  platform,
  subscriptions = [],
  stations = [],
  settings = null,
  quietWindows = [],
  rules = [],
  tzOffsetMinutes = null,
  city = 'melbourne',
  notices = false,
  purchase = null,
  modeStops = [],
} = {}) {
  const body = {
    token: String(token ?? ''),
    platform: platform === 'ios' ? 'ios' : 'android',
    // Which city this phone's lines and stations belong to.
    //
    // THE SERVER IGNORES IT TODAY, and it is sent anyway. The sweep polls one
    // disruption feed and matches by route id; the moment there are two feeds
    // it has to know which phones to wake for which, and the alternative to
    // sending it now is a migration that guesses — route ids are globally
    // unique, so it could be inferred, but "inferred" is how a Sydney phone
    // ends up on the Melbourne sweep with nothing on any screen to say why.
    // Defaulted rather than required, so a caller that predates this field
    // keeps meaning exactly what it meant.
    city: String(city ?? 'melbourne'),
    lines: ids(subscriptions.filter((s) => s?.active !== false).map((s) => s.line)),
    stations: ids(stations.map((s) => s.stopId)),
    // `new Date().getTimezoneOffset()` is minutes to ADD to local to get UTC —
    // +660 behind AEDT. Negated here so the field means what it says: minutes
    // EAST of UTC, so Melbourne is +600 and the sweep adds it.
    tzOffsetMinutes: Number.isFinite(Number(tzOffsetMinutes)) ? Number(tzOffsetMinutes) : 0,
    // The two lists the sweep actually decides on. Always present, even empty:
    // an absent `rules` and an empty `rules` mean different things to
    // `shouldNotify` — no rules is "tell me about my lines", which is the old
    // behaviour — and a body whose shape depends on the data is a body the
    // server has to guess about.
    quietWindows: windowsFor(quietWindows),
    rules: rulesFor(rules),
    // Network notices (src/notices.js): Royal Show trains, holiday timetables.
    // Opt-in, so false unless the person turned it on.
    notices: notices === true,
    // The stops this phone uses on each watched route of a mode (tram, bus), so
    // the sweep can leave a diversion at the far end of a route as a quiet row. Always
    // present, even empty: an empty list is "none starred", which must replace
    // what the server holds from before the last one was unstarred.
    modeStops: modeStopsFor(modeStops),
  };

  // The store's proof that this phone paid, when it has one. See
  // src/billing.js for its two shapes. Absent rather than null otherwise, so a
  // server that predates the paywall sees the body it always saw.
  const proof = purchaseFor(purchase);
  if (proof) body.purchase = proof;

  const from = String(settings?.quietFrom ?? '').trim();
  const to = String(settings?.quietUntil ?? '').trim();
  if (from && to) {
    body.quiet = { from, to, weekdaysOnly: !!settings?.weekdaysOnly };
  } else if (settings?.weekdaysOnly) {
    // Weekends-off with no overnight window is a real setting, and dropping it
    // because `from` was blank is how somebody gets alerted on a Sunday they
    // explicitly opted out of.
    body.quiet = { from: '', to: '', weekdaysOnly: true };
  }
  return body;
}

/**
 * The purchase proof, trimmed to the contract's two shapes, or null.
 *
 * Copied field by field rather than passed through: whatever else the plugin
 * put on the object (an expiry, an acknowledged flag) is for this phone and
 * has no business in a database.
 */
export function purchaseFor(value) {
  if (!value || typeof value !== 'object') return null;
  if (value.store === 'apple' && typeof value.jws === 'string' && value.jws) {
    return { store: 'apple', jws: value.jws };
  }
  if (value.store === 'google' && typeof value.purchaseToken === 'string' && value.purchaseToken) {
    return {
      store: 'google',
      productId: String(value.productId || 'tellme'),
      basePlanId: value.basePlanId ? String(value.basePlanId) : null,
      purchaseToken: value.purchaseToken,
    };
  }
  return null;
}

/**
 * Caps. A subscribe body crosses a mobile network on a phone at a barrier, and
 * a data fault must not be able to put a megabyte of stops in one.
 */
const MAX_WINDOWS = 10;
const MAX_RULES = 20;
const MAX_PATH_STOPS = 160;
const MAX_FALLBACK_ROUTES = 20;
/** One line on a lock screen. `HEADLINE_MAX` in `src/reroute.js` is the same. */
const MAX_FALLBACK_TEXT = 120;

/** Whole days only, 0..6, JS `getDay()` order — Sunday is 0 on both sides. */
function days(value) {
  const out = [];
  for (const day of Array.isArray(value) ? value : []) {
    const n = Number(day);
    if (!Number.isInteger(n) || n < 0 || n > 6 || out.includes(n)) continue;
    out.push(n);
  }
  return out.sort((a, b) => a - b);
}

/**
 * `HH:MM` in 24-hour time, or an empty string.
 *
 * THE SAME PATTERN THE SERVER VALIDATES WITH, deliberately copied rather than
 * loosened. A body it refuses is refused WHOLE: one `8:00` written by hand into
 * a stored window would 400 the subscribe and take the watched lines and saved
 * stations down with it, and the phone would simply stop being pushed to with
 * nothing on any screen to say why.
 */
const HHMM = /^(?:[01]\d|2[0-3]):[0-5]\d$/;

function clock(value) {
  const text = String(value ?? '').trim();
  return HHMM.test(text) ? text : '';
}

/**
 * An id the server will accept, and that a log line can carry.
 *
 * Same alphabet as its `RULE_ID`. Ids are generated on this phone and have
 * never been anything else, so this only ever matters for a row that has been
 * through storage somebody edited — which is exactly the row that must not be
 * able to silence the whole device.
 */
function safeId(value) {
  return String(value ?? '').replace(/[^A-Za-z0-9:._-]/g, '-').slice(0, 64);
}

/** Minutes of lead, inside the range the server accepts. */
const MAX_LEAD_MINUTES = 180;

function lead(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.min(MAX_LEAD_MINUTES, Math.max(0, Math.round(n)));
}

/** `[{ routeId, stops }]`, trimmed to what the server accepts. */
function modeStopsFor(list) {
  return (Array.isArray(list) ? list : []).slice(0, 50)
    .map((r) => ({ routeId: Number(r?.routeId), stops: ids(r?.stops, MAX_PATH_STOPS) }))
    .filter((r) => Number.isInteger(r.routeId) && r.routeId > 0 && r.stops.length);
}

/**
 * The quiet windows, as the server needs them.
 *
 * A window missing either end silences nothing — `inAnyQuietWindow` reads it
 * as malformed and skips it — so sending it would only be sending noise.
 */
function windowsFor(list) {
  return (Array.isArray(list) ? list : [])
    .filter((w) => clock(w?.from) && clock(w?.to))
    .slice(0, MAX_WINDOWS)
    .map((w) => ({
      id: safeId(w.id),
      from: clock(w.from),
      to: clock(w.to),
      days: days(w.days),
    }));
}

/**
 * What to do instead, if this rule came with an answer.
 *
 * `{ text, routeIds }` and NOTHING ELSE. The sentence was written on this
 * phone by `fallbackHeadline`, out of a home station and a walking radius that
 * do not leave it — the server is handed the conclusion and never the premise.
 * Anything that is not a usable headline is sent as `null`: the server refuses
 * to 400 over this field, and the phone should not manufacture one either.
 */
function fallbackFor(value) {
  const text = String(value?.text ?? '').trim().slice(0, MAX_FALLBACK_TEXT);
  if (!text) return null;
  const routeIds = ids(value?.routeIds ?? [], MAX_FALLBACK_ROUTES);
  if (!routeIds.length) return null;
  return { text, routeIds };
}

/**
 * The rules, trimmed to what the sweep reads.
 *
 * `fromName`/`toName` are NOT sent. They exist so a screen can show what it
 * matched; the server decides with `path` and never prints a rule, so sending
 * them would put somebody's home station in a database for no purpose at all.
 *
 * A rule with no resolved path is dropped rather than sent: it matches nothing
 * at any of `touches`'s three tiers, so it can only ever cost the sweep work.
 */
function rulesFor(list) {
  return (Array.isArray(list) ? list : [])
    .filter((r) => Array.isArray(r?.path?.stops) && r.path.stops.length
      && clock(r.start) && clock(r.end))
    .slice(0, MAX_RULES)
    .map((r) => ({
      id: safeId(r.id),
      from: Number(r.from) || 0,
      to: Number(r.to) || 0,
      start: clock(r.start),
      end: clock(r.end),
      lead: lead(r.lead),
      days: days(r.days),
      active: r.active !== false,
      mutedUntil: typeof r.mutedUntil === 'string' && r.mutedUntil ? r.mutedUntil : null,
      path: {
        stops: ids(r.path.stops, MAX_PATH_STOPS),
        routes: ids(r.path.routes ?? []),
      },
      // Computed fresh in `subscribeNow` and never stored on the rule: it
      // depends on where home is today and on which lines the rule rides, and
      // a stale one would be advice about a commute somebody has changed.
      fallback: fallbackFor(r.fallback),
    }));
}

/**
 * One POST. `{ ok, status?, error?, data? }`, and it never throws.
 *
 * `data` is the parsed 2xx body, which used to be thrown away because nothing
 * read it. The mute routes answer with the instant the mute ends, and that
 * instant is computed on the SERVER — from the rules it holds, in the timezone
 * it was told — so the phone has to be told what it got rather than working it
 * out a second time and disagreeing about the minute.
 */
async function post(url, body, fetchImpl) {
  const send = fetchImpl ?? (typeof fetch === 'function' ? fetch : null);
  if (!send) return { ok: false, error: 'no fetch' };
  try {
    const res = await send(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal: AbortSignal.timeout(12_000),
    });
    if (!res.ok) {
      // The server names the offending field on a 400; carrying it back is the
      // difference between a fixable bug report and "alerts don't work".
      const detail = await res.json?.().catch(() => null);
      return {
        ok: false,
        status: res.status,
        error: detail?.error ?? `HTTP ${res.status}`,
        ...(detail?.reason ? { reason: String(detail.reason) } : {}),
      };
    }
    const data = await res.json?.().catch(() => null);
    return { ok: true, status: res.status ?? 200, data: data ?? null };
  } catch (e) {
    return { ok: false, error: e?.message ?? 'unreachable' };
  }
}

export function postSubscribe(base, body, { fetchImpl } = {}) {
  return post(`${base}/api/push/subscribe`, body, fetchImpl);
}

export function postUnsubscribe(base, token, { fetchImpl } = {}) {
  return post(`${base}/api/push/unsubscribe`, { token: String(token ?? '') }, fetchImpl);
}

// ── The mute a notification's quick actions set ──────────────────────────────
//
// Two buttons under a disruption alert: "Mute this window" and "Mute today".
// The NATIVE side answers them first — a button pressed on the lock screen must
// work with the app not running at all — and these exist because the same
// buttons are also pressed while the app is open, where the JavaScript side
// hears the action and the native handler may or may not have already called.
//
// Both calls are therefore IDEMPOTENT by construction: the server recomputes
// the end from the same rules and writes it, so the same press landing twice
// writes the same instant twice.

export function postMute(base, { token, platform, until } = {}, { fetchImpl } = {}) {
  return post(`${base}/api/push/mute`, {
    token: String(token ?? ''),
    platform: platform === 'ios' ? 'ios' : 'android',
    until: until === 'day' ? 'day' : 'window',
  }, fetchImpl);
}

export function postUnmute(base, { token, platform } = {}, { fetchImpl } = {}) {
  return post(`${base}/api/push/unmute`, {
    token: String(token ?? ''),
    platform: platform === 'ios' ? 'ios' : 'android',
  }, fetchImpl);
}

/**
 * The mute a response describes: `{ until, by }`, or null for "it did not say".
 *
 * THE THREE ANSWERS ARE DIFFERENT AND ALL THREE MATTER:
 *
 *   `{ until: '…', by: 'day' }`  muted until then.
 *   `{ until: null, by: null }`  not muted, said explicitly. This is what
 *                                clears a stale local mute after `/unmute`, and
 *                                after a subscribe to a server that has already
 *                                let one expire.
 *   `null`                       the response says NOTHING about muting — a
 *                                failed call, or a server too old to have the
 *                                field. The caller must leave what it has
 *                                alone: overwriting it with "not muted" would
 *                                un-silence a phone because the network was bad.
 *
 * It takes either the raw server JSON or the `{ ok, data }` wrapper above, so
 * the caller does not have to unwrap before asking.
 */
export function muteStateFrom(response) {
  if (!response || typeof response !== 'object') return null;
  if (response.ok === false) return null;

  const body = response.data && typeof response.data === 'object' ? response.data : response;
  if (!body || typeof body !== 'object' || !('mutedUntil' in body)) return null;

  const raw = body.mutedUntil;
  if (raw === null || raw === undefined || raw === '') return { until: null, by: null };

  const ms = Date.parse(String(raw));
  if (!Number.isFinite(ms)) return null;

  const by = body.mutedBy === 'window' || body.mutedBy === 'day' ? body.mutedBy : null;
  return { until: new Date(ms).toISOString(), by };
}

/** Is this stored mute still running? An expired one is simply over. */
export function muteHolds(state, now = Date.now()) {
  const ms = Date.parse(state?.until ?? '');
  return Number.isFinite(ms) && ms > now;
}

/**
 * Coalesce a burst of calls into one, `ms` after the last.
 *
 * Ticking six lines on the Choose lines screen is six store writes in about
 * four seconds. Six subscribe POSTs would all be correct and five would be
 * wasted, and the phone is usually on mobile data when it happens.
 *
 * Timers are injectable so the test does not have to sleep.
 */
export function debounced(fn, ms = 2000, timers = {}) {
  const set = timers.setTimeout ?? setTimeout;
  const clear = timers.clearTimeout ?? clearTimeout;
  let handle = null;
  const run = (...args) => {
    if (handle !== null) clear(handle);
    handle = set(() => { handle = null; fn(...args); }, ms);
  };
  run.cancel = () => { if (handle !== null) clear(handle); handle = null; };
  return run;
}
